// frontend/src/features/content/contentApiSlice.js
import {
    createSelector,
    createEntityAdapter
} from "@reduxjs/toolkit";
import { apiSlice } from "../../app/api/apiSlice";

const contentAdapter = createEntityAdapter({
    // कंटेंट को उनके शीर्षक (title) के अनुसार सॉर्ट करें
    sortComparer: (a, b) => a.title.localeCompare(b.title)
});

const initialState = contentAdapter.getInitialState();

export const contentApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        // Admin View: सभी कंटेंट प्राप्त करें (pagination, search, filters के साथ)
        getAllContentAdmin: builder.query({
            query: ({ page = 1, search = '', isActive = '', category = '', Clanguage = '' }) => {
                const params = new URLSearchParams();
                params.append('page', page.toString());
                if (search) params.append('keyword', search);
                if (isActive !== '') params.append('isActive', isActive);
                if (category) params.append('category', category);
                if (Clanguage) params.append('Clanguage', Clanguage);
                const url = `/api/v1/admin/items?${params.toString()}`;
                console.log('contentApiSlice - API Request URL:', url);
                return {
                    url: url,
                    method: 'GET',
                };
            },
            transformResponse: (responseData, meta, arg) => { // ✅ arg (query arguments) को यहाँ पास किया गया है
                console.log('contentApiSlice - Raw API Response Data:', responseData);

                // ✅ मुख्य सुधार: responseData.items के बजाय responseData.contents का उपयोग करें
                const data = responseData.contents || [];

                const loadedContent = data.map(content => {
                    return {
                        id: content._id,
                        ...content
                    };
                });

                // ✅ totalPages और currentPage की गणना करें यदि वे सीधे API से नहीं आ रहे हैं
                const calculatedTotalPages = responseData.resultPerPage > 0
                    ? Math.ceil(responseData.contentCount / responseData.resultPerPage)
                    : 0; // यदि resultPerPage 0 है तो 0 पेज

                const currentPageFromArgs = arg.page; // ✅ current page को query arguments से लें

                return {
                    ...contentAdapter.setAll(initialState, loadedContent),
                    totalPages: calculatedTotalPages, // ✅ गणना किया गया totalPages
                    currentPage: currentPageFromArgs, // ✅ query arguments से currentPage
                    filteredContentsCount: responseData.filteredContentCount,
                };
            },
            providesTags: (result) =>
                result?.ids
                    ? [
                        { type: 'Content', id: 'LIST_ADMIN' },
                        ...result.ids.map(id => ({ type: 'Content', id }))
                    ]
                    : [{ type: 'Content', id: 'LIST_ADMIN' }]
        }),

        // Admin View: एक विशिष्ट कंटेंट प्राप्त करें (एडिट के लिए)
        getContentDetailAdmin: builder.query({
            query: (contentId) => ({
                url: `/api/v1/admin/items/${contentId}`,
                method: 'GET',
            }),
            transformResponse: responseData => {
                if (responseData.item) {
                    return contentAdapter.upsertOne(initialState, {
                        id: responseData.item._id,
                        ...responseData.item
                    });
                }
                return initialState;
            },
            providesTags: (result, error, arg) => [
                { type: 'Content', id: arg }
            ]
        }),

        // नया कंटेंट बनाएं
        addNewContent: builder.mutation({
            query: contentData => ({
                url: '/api/v1/admin/items',
                method: 'POST',
                body: contentData,
            }),
            invalidatesTags: [
                { type: 'Content', id: "LIST_ADMIN" }
            ]
        }),

        // कंटेंट अपडेट करें
        updateContent: builder.mutation({
            query: ({ id, formData }) => ({
                url: `/api/v1/admin/items/${id}`,
                method: 'PUT',
                body: formData, // ✅ Directly use formData
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Content', id: arg.id },
                { type: 'Content', id: 'LIST_ADMIN' }
            ]
        }),

        // कंटेंट डिलीट करें
        deleteContent: builder.mutation({
            query: ({ id }) => ({
                url: `/api/v1/admin/items/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'Content', id: arg.id },
                { type: 'Content', id: 'LIST_ADMIN' }
            ]
        }),

        // Public View: सभी कंटेंट प्राप्त करें (यदि आवश्यक हो) - यह optional है
        getContentsPublic: builder.query({
            query: ({ page = 1, search = '', category = '' }) => {
                const params = new URLSearchParams();
                params.append('page', page.toString());
                if (search) params.append('search', search);
                if (category) params.append('category', category);
                return {
                    url: `/api/v1/items?${params.toString()}`,
                    method: 'GET',
                };
            },
            transformResponse: responseData => {
                const data = responseData.items || [];
                const loadedContent = data.map(content => {
                    return {
                        id: content._id,
                        ...content
                    };
                });
                return contentAdapter.setAll(initialState, loadedContent);
            },
            providesTags: (result) =>
                result?.ids
                    ? [
                        { type: 'Content', id: 'LIST_PUBLIC' },
                        ...result.ids.map(id => ({ type: 'Content', id }))
                    ]
                    : [{ type: 'Content', id: 'LIST_PUBLIC' }]
        }),
    }),
});

export const {
    useGetAllContentAdminQuery,
    useGetContentDetailAdminQuery,
    useAddNewContentMutation,
    useUpdateContentMutation,
    useDeleteContentMutation,
    useGetContentsPublicQuery,
} = contentApiSlice;

// ***************************************************************
// ** SELECTORS FOR ADMIN VIEW (Same pattern as categories)    **
// ***************************************************************

// यह सिलेक्टर क्वेरी आर्ग्यूमेंट्स (जैसे { page: 1, search: '', isActive: '', category: '' }) लेता है
// और उन आर्ग्यूमेंट्स के लिए विशिष्ट कैश रिजल्ट रिटर्न करता है।
export const selectContentResultByArgs = createSelector(
    (state, queryArgs) => contentApiSlice.endpoints.getAllContentAdmin.select(queryArgs)(state),
    contentResult => {
        const data = contentResult?.data ?? {
            ...contentAdapter.getInitialState(),
            totalPages: 0,
            currentPage: 1,
            filteredContentsCount: 0
        };
        return data;
    }
);

// यह सिलेक्टर एडमिन व्यू के लिए ID द्वारा एक सिंगल कंटेंट आइटम प्राप्त करता है।
export const selectContentByIdForAdmin = createSelector(
    (state, queryArgs) => selectContentResultByArgs(state, queryArgs),
    (state, queryArgs, contentId) => contentId,
    (contentData, contentId) => contentAdapter.getSelectors().selectById(contentData, contentId)
);

export const selectAllContentForAdminByArgs = createSelector(
    (state, queryArgs) => selectContentResultByArgs(state, queryArgs),
    (contentData) => contentAdapter.getSelectors().selectAll(contentData)
);


// ***************************************************************
// ** PUBLIC CONTENT SELECTORS (Same pattern as categories)    **
// ***************************************************************
export const selectContentsPublicResult = contentApiSlice.endpoints.getContentsPublic.select();

const selectPublicContentsData = createSelector(
    selectContentsPublicResult,
    contentsResult => contentsResult.data ?? contentAdapter.getInitialState()
);

const selectPublicContentsNormalizedState = createSelector(
    selectPublicContentsData,
    (data) => {
        return data ? { ids: data.ids, entities: data.entities } : contentAdapter.getInitialState();
    }
);

export const {
    selectAll: selectAllContentsForPublic,
    selectById: selectContentByIdForPublic,
    selectIds: selectContentIdsForPublic,
} = contentAdapter.getSelectors(state => selectPublicContentsNormalizedState(state));